import {Component, Input, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {LangService} from 'ngx-satoris';
import {extractCountryCode} from '../../utils/phone';
import codes from 'country-calling-code';
import {environment} from 'src/environments/environment';

@Component({
  selector: 'app-phone-country-select',
  template: `
    <div class="phone-input-wrapper">
      <div class="country-selector-wrapper">
        <app-input
          type="select"
          [formGrp]="internalForm"
          formCtrlName="prefix"
          [selectOptions]="countryOptions"
          [disabled]="disabled"
          icon="flag"
          (change)="onPrefixChange($event)"
          />
        </div>
      
      <div id="c-prefix">{{ prefix }}</div>

      <app-input
        type="tel"
        class="phone-number-input"
        [formGrp]="internalForm"
        formCtrlName="phone"
        [label]="label"
        [disabled]="disabled || isPrefixEmpty"
        icon="phone"
        maxlength="15"
        (input)="onPhoneInput($event)"
      />
    </div>
  `,
  styles: [`
    .phone-input-wrapper { display: flex; align-items: stretch; gap: 8px; align-items: center; }
    .country-selector-wrapper { flex: 1; }
    .phone-number-input { flex: 2; min-width: 120px; }
    #c-prefix { width: 50px; }
    .phone-input-wrapper ::ng-deep .input_wrapper select { 
      padding-inline-end: 10px; 
    }
  `]
})
export class PhoneCountrySelectComponent implements OnInit {
  @Input() formGrp: FormGroup;
  @Input() formCtrlName: string;
  @Input() label: string;
  @Input() disabled = false;

  countryOptions: { name: string, value: string }[] = [];
  isPrefixEmpty = true;
  internalForm: FormGroup;
  private originalPhoneValidators: any[];

  constructor(private formBuilder: FormBuilder, private lang: LangService) {}

  ngOnInit(): void {
    this.internalForm = this.formBuilder.group({
      prefix: ['+', [Validators.required]],
      phone: [{value: '', disabled: true}, [Validators.required]]
    });

    this.originalPhoneValidators = this.formGrp.get('phone')?.validator ? [this.formGrp.get('phone')?.validator] : [];

    const countryNameTranslated = new Intl.DisplayNames([this.lang.readLangString], {type: 'region'});

    this.countryOptions = [
      {name: this.lang.transform('form.selectCountry'), value: '+'},
      ...codes.map(({isoCode2, countryCodes}) => ({
        name: `${countryNameTranslated.of(isoCode2)} (+${countryCodes?.[0]})`,
        value: `+${countryCodes?.[0]}`
      })).sort((a, b) => a.name.localeCompare(b.name))
    ];
    const existingValue = this.formGrp.get(this.formCtrlName)?.value;
    if(existingValue) {
      this.parsePhoneValue(existingValue);
    }
    this.internalForm.get('prefix')?.valueChanges.subscribe(() => this.updateParentFormGrp());
    this.internalForm.get('phone')?.valueChanges.subscribe(() => this.updateParentFormGrp());
    this.updatePhoneEnableState();

  }
  
  get prefix() {
    return this.internalForm.get('prefix')?.value;
  }

  onPrefixChange(_event: any): void {
    this.applyPhoneValidation();
    this.updatePhoneEnableState();
  }

  onPhoneInput(event: any): void {
    let clean = (event.target.value || '').replace(/[^0-9]/g, '');
    this.applyPhoneValidation();
    this.internalForm.get('phone')?.setValue(clean, {emitEvent: false});
    this.updateParentFormGrp();
  }

  private updatePhoneEnableState(): void {
    const prefix = this.internalForm.get('prefix')?.value;
    this.isPrefixEmpty = !prefix || prefix === '+';
    
    if(this.isPrefixEmpty) {
      this.internalForm.get('phone')?.disable();
    } else {
      this.internalForm.get('phone')?.enable();
    }
  }

  private parsePhoneValue(value: string): void {
    if(!value) return;
    
    const extractedCode = extractCountryCode(value);
    if(extractedCode) {
      const prefix = `+${extractedCode}`;
      const phoneWithoutPrefix = value.replace(prefix, '');
      this.internalForm.get('prefix')?.setValue(prefix, {emitEvent: false});
      this.internalForm.get('phone')?.setValue(phoneWithoutPrefix, {emitEvent: false});
      this.internalForm.get('phone')?.enable();
      this.isPrefixEmpty = false;
    }
  }

  private updateParentFormGrp(): void {
    const prefix = this.internalForm.get('prefix')?.value?.replace('-', '');
    const phone = this.internalForm.get('phone')?.value?.replace(/^0+/, '');;
    const fullPhone = (prefix && prefix !== '+' && phone) ? `${prefix}${phone}` : null;
    this.formGrp.get(this.formCtrlName)?.setValue(fullPhone, {emitEvent: false});
  }

  private applyPhoneValidation(): void {
    const phone = this.formGrp.get(this.formCtrlName);
    const phoneValidator = environment.countryBasedPhonePattern?.[this.prefix];
    if(phoneValidator) {
      phone?.addValidators(this.phonePatternValidator(phoneValidator.pattern, phoneValidator.translationKey));
    } else {
      phone?.setValidators(this.originalPhoneValidators);
    }
    phone?.updateValueAndValidity();
  }

  private phonePatternValidator(pattern: RegExp, errorKey?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if(!control.value) return null;
      return pattern.test(control.value) ? null : {[errorKey ?? 'pattern']: true};
    };
  }
}